import type { ExamType } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";

/**
 * Maps GE VScan Air ImageType[2] preset values to ePocus exam types.
 *
 * null = ambiguous or no ePocus worksheet — cannot be auto-resolved.
 *
 * PNE-212: used for:
 *   1. The "Document Now" exam type picker (ambiguous presets → show feasible options)
 *   2. Conflict blocking (unambiguous preset conflicts with pre-selected exam type)
 */
export const GE_PRESET_TO_EXAM_TYPE: Partial<Record<string, ExamType | null>> =
  {
    TTE: "cardiac",
    CHEST: null, // ambiguous — could be lung or efast
    VASCULAR: "aorta",
    ABDOMINAL: null, // could be efast | renal | bladder
    "SMALL PARTS": null, // could be renal | bladder
    OBSTETRICAL: "pregnancy",
    MUSCULOSKELETAL: null, // no ePocus worksheet
    "NEONATAL HEAD": null, // no ePocus worksheet
  };

/**
 * Feasible exam types for each ambiguous GE preset.
 * Only populated for presets where the set of plausible ePocus exam types is known.
 */
const GE_PRESET_FEASIBLE_EXAM_TYPES: Partial<Record<string, ExamType[]>> = {
  CHEST: ["lung", "efast"],
  ABDOMINAL: ["efast", "renal", "bladder"],
  "SMALL PARTS": ["renal", "bladder"],
};

/**
 * Returns true when the DICOM preset unambiguously indicates a different exam type
 * from the one the user has committed to.
 *
 * Returns false when:
 *  - The preset is unknown (not in the mapping)
 *  - The preset is ambiguous (maps to null)
 *  - The preset matches the exam type
 */
export function isPresetConflict(
  dicomPreset: string,
  examType: ExamType,
): boolean {
  const mappedType = GE_PRESET_TO_EXAM_TYPE[dicomPreset];
  // Unknown preset or ambiguous preset — cannot determine conflict
  if (mappedType === undefined || mappedType === null) return false;
  return mappedType !== examType;
}

/**
 * Returns the feasible exam type options for a given preset.
 * - For unambiguous presets: returns [singleExamType]
 * - For ambiguous presets with known options: returns the feasible set
 * - For presets with no worksheet mapping: returns all exam types (full picker)
 * - For unknown presets: returns all exam types (full picker)
 */
export function getFeasibleExamTypes(dicomPreset: string): ExamType[] | null {
  const mappedType = GE_PRESET_TO_EXAM_TYPE[dicomPreset];

  if (mappedType === undefined) {
    // Completely unknown preset — full picker
    return null;
  }
  if (mappedType !== null) {
    // Unambiguous: single exam type
    return [mappedType];
  }
  // Ambiguous: known feasible set or null (full picker)
  return GE_PRESET_FEASIBLE_EXAM_TYPES[dicomPreset] ?? null;
}
